import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
} from "@mui/material";

export interface PotentialDuplicateItem {
  /** Stable key for the list item. */
  id: string;
  /** Destination for the link (opened in a new tab). */
  href: string;
  /** Text shown for the link. */
  label: string;
}

export interface PotentialDuplicateWarningDialogProps {
  open: boolean;
  /** Heading. Defaults to "Warning: Potential Duplicate". */
  title?: string;
  /** Body copy explaining what was matched. */
  message: string;
  /** Existing records that may be duplicates, rendered as links. */
  items: PotentialDuplicateItem[];
  cancelLabel: string;
  continueLabel: string;
  onCancel: () => void;
  onContinue: () => void;
  /**
   * Invoked on backdrop / Escape dismissal. Defaults to `onContinue` for
   * advisory dialogs; pass a distinct handler when continuing has a side
   * effect (e.g. committing a save) that dismissal should not trigger.
   */
  onClose?: () => void;
}

export function PotentialDuplicateWarningDialog({
  open,
  title = "Warning: Potential Duplicate",
  message,
  items,
  cancelLabel,
  continueLabel,
  onCancel,
  onContinue,
  onClose,
}: PotentialDuplicateWarningDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose ?? onContinue}
      slotProps={{
        paper: { sx: { "&:focus, &:focus-visible": { outline: "none" } } },
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
        {items.length > 0 && (
          <DialogContentText sx={{ mt: 1 }}>
            {items.map((item, index) => (
              <span key={item.id}>
                {index > 0 && ", "}
                <Link
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="secondary"
                >
                  {item.label}
                </Link>
              </span>
            ))}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button variant="contained" color="primary" onClick={onContinue}>
          {continueLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
